/**
 * Case Conversion Utilities
 *
 * Provides functions to convert between camelCase (JavaScript convention)
 * and snake_case (Python/API convention).
 *
 * Per SPECIFICATION.md Section 1.1:
 * - Response bodies use camelCase (handled by backend Pydantic alias)
 * - Query parameters use snake_case (must be converted by frontend)
 *
 * @see docs/spec/api-surface.md Section 1.1 JSON Serialization Convention
 */

/**
 * Convert a snake_case string to camelCase.
 *
 * Examples:
 *   start_date     -> startDate
 *   end_date       -> endDate
 *   account_id     -> accountId
 *   real_estate    -> realEstate
 *   other_asset    -> otherAsset
 *   is_active      -> isActive
 *   already_camel  -> alreadyCamel (converts anyway)
 *   simple         -> simple (no change)
 *
 * @param str - The snake_case string to convert
 * @returns The camelCase equivalent
 */
export function snakeToCamel(str: string): string {
  // Handle empty or non-string input
  if (!str || typeof str !== 'string') {
    return str;
  }

  // Replace _x with X (uppercase the letter after underscore)
  // Regex explanation:
  // - _([a-z]) matches underscore followed by lowercase letter
  // - We replace it with the uppercase version of that letter
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case.
 *
 * Examples:
 *   startDate     -> start_date
 *   endDate       -> end_date
 *   accountId     -> account_id
 *   pageSize      -> page_size
 *   isActive      -> is_active
 *   HTTPResponse  -> h_t_t_p_response (edge case, unlikely in our API)
 *   already_snake -> already_snake (no change)
 *   simple        -> simple (no change)
 *
 * @param str - The camelCase string to convert
 * @returns The snake_case equivalent
 */
export function camelToSnake(str: string): string {
  // Handle empty or non-string input
  if (!str || typeof str !== 'string') {
    return str;
  }

  // Insert underscore before each uppercase letter and convert to lowercase
  // Regex explanation:
  // - ([A-Z]) matches any uppercase letter
  // - We replace it with _<lowercase letter>
  // - Then trim any leading underscore (in case string starts with uppercase)
  return str
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, ''); // Remove leading underscore if string started with capital
}

/**
 * Convert all keys in an object from camelCase to snake_case.
 *
 * This is used to transform query parameters before sending to the API.
 * Values are preserved as-is (not transformed).
 *
 * @param obj - Object with camelCase keys
 * @returns New object with snake_case keys
 */
export function transformKeysToSnakeCase(
  obj: Record<string, unknown> | undefined | null
): Record<string, unknown> | undefined {
  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return undefined;
  }

  // Handle non-object types (shouldn't happen, but be defensive)
  if (typeof obj !== 'object') {
    return obj as unknown as Record<string, unknown>;
  }

  // Handle arrays - shouldn't be passed as params object, but be defensive
  if (Array.isArray(obj)) {
    return obj as unknown as Record<string, unknown>;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = camelToSnake(key);
    // Don't transform values - only keys
    // Arrays and primitives pass through as-is
    result[snakeKey] = value;
  }

  return result;
}
